import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Crown, Shield, Users as UsersIcon, Building2, Trash2, Pencil, Power,
  Globe, Search, ArrowLeft, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "users", label: "Benutzer", Icon: UsersIcon },
  { key: "clubs", label: "Vereine", Icon: Building2 },
  { key: "teams", label: "Mannschaften", Icon: Shield },
];

export default function SuperAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");

  useEffect(() => {
    if (user && !user.is_super_admin) navigate("/", { replace: true });
  }, [user, navigate]);

  if (!user?.is_super_admin) return null;

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-6xl mx-auto" data-testid="super-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}
          className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900" data-testid="sa-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-amber-400 flex items-center gap-1">
            <Crown className="h-3 w-3" /> Super-Admin
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase">Plattform-Verwaltung</h1>
          <p className="text-xs text-zinc-500 mt-1">Globaler Zugriff über alle Vereine, Mannschaften und Nutzer hinweg.</p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-zinc-800 overflow-x-auto" data-testid="sa-tabs">
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={"flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border-b-2 transition whitespace-nowrap " +
              (tab === key
                ? "text-amber-400 border-amber-400"
                : "text-zinc-400 border-transparent hover:text-zinc-100")}
            data-testid={`sa-tab-${key}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersTab currentUserId={user.id} />}
      {tab === "clubs" && <ClubsTab />}
      {tab === "teams" && <TeamsTab />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// USERS
// -----------------------------------------------------------------------------
function UsersTab({ currentUserId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.name + " " + u.email + " " + (u.club_name || "")).toLowerCase().includes(q);
  });

  return (
    <div data-testid="sa-users">
      <SearchBar value={search} onChange={setSearch} placeholder="Name, E-Mail, Verein…" testId="sa-users-search" />
      {loading ? <Loading /> : (
        <div className="space-y-2 mt-3">
          {filtered.length === 0 && <Empty />}
          {filtered.map((u) => (
            <div key={u.id}
              className={"rounded-md border bg-zinc-900/40 p-4 " + (u.active ? "border-zinc-800" : "border-zinc-800/50 opacity-60")}
              data-testid={`sa-user-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className={"h-10 w-10 flex items-center justify-center rounded-md shrink-0 " +
                    (u.is_super_admin ? "bg-amber-500/20" : u.role === "club_admin" ? "bg-brand/20" : "bg-zinc-800")}>
                    {u.is_super_admin
                      ? <Crown className="h-5 w-5 text-amber-400" />
                      : u.role === "club_admin"
                        ? <Shield className="h-5 w-5 text-brand-lighter" />
                        : <UsersIcon className="h-5 w-5 text-zinc-400" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-zinc-100 truncate flex items-center gap-2 flex-wrap">
                      {u.name || u.email}
                      {u.id === currentUserId && <span className="text-[10px] uppercase tracking-wider text-amber-400">(Du)</span>}
                      {u.is_super_admin && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-semibold">Super-Admin</span>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                      {u.role === "club_admin" ? "Vereinsadmin" : "Trainer"}
                      {" · "}{u.active ? "Aktiv" : "Deaktiviert"}
                      {u.club_name && <> · <span className="text-zinc-400 normal-case">{u.club_name}</span></>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditItem(u)}
                    className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800"
                    data-testid={`sa-user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                  {u.id !== currentUserId && (
                    <Button variant="ghost" size="sm" onClick={() => setDelItem(u)}
                      className="text-zinc-400 hover:text-red-400 hover:bg-zinc-800"
                      data-testid={`sa-user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <UserEditDialog item={editItem} currentUserId={currentUserId}
        onClose={() => setEditItem(null)} onSaved={async () => { setEditItem(null); await load(); }} />
      <ConfirmDeleteDialog item={delItem}
        title={`Nutzer "${delItem?.name || delItem?.email}" löschen?`}
        description="Permanent. Memberships werden mitgelöscht. Daten von Vereinen/Teams bleiben erhalten."
        onClose={() => setDelItem(null)}
        onConfirm={async () => {
          try {
            await api.delete(`/super/users/${delItem.id}`);
            toast.success("Nutzer gelöscht");
            setDelItem(null);
            await load();
          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen"); }
        }} />
    </div>
  );
}

function UserEditDialog({ item, currentUserId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("trainer");
  const [active, setActive] = useState(true);
  const [isSA, setIsSA] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setName(item.name || ""); setRole(item.role || "trainer");
      setActive(!!item.active); setIsSA(!!item.is_super_admin);
    }
  }, [item]);
  if (!item) return null;
  const isSelf = item.id === currentUserId;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/super/users/${item.id}`, {
        name: name.trim(), role, active, is_super_admin: isSA,
      });
      toast.success("Nutzer aktualisiert");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle>Nutzer bearbeiten</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">{item.email} {item.club_name && `· ${item.club_name}`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="sa-user-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Rolle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="sa-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="trainer">Trainer</SelectItem>
                <SelectItem value="club_admin">Vereinsadmin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
            <div>
              <div className="text-sm text-zinc-100">Aktiv</div>
              <div className="text-[11px] text-zinc-500">Deaktivierte Nutzer können sich nicht einloggen.</div>
            </div>
            <input type="checkbox" checked={active} disabled={isSelf}
              onChange={(e) => setActive(e.target.checked)} className="accent-brand h-4 w-4" data-testid="sa-user-active" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-amber-900/40 bg-amber-900/10 p-3">
            <div>
              <div className="text-sm text-amber-200 flex items-center gap-1.5"><Crown className="h-3.5 w-3.5" />Super-Admin</div>
              <div className="text-[11px] text-amber-200/70">Vollzugriff plattformweit. Mit Bedacht vergeben.</div>
            </div>
            <input type="checkbox" checked={isSA} disabled={isSelf}
              onChange={(e) => setIsSA(e.target.checked)} className="accent-amber-500 h-4 w-4" data-testid="sa-user-is-sa" />
          </div>
          {isSelf && <div className="text-[11px] text-zinc-500">Eigene Aktiv/Super-Admin-Flags nicht änderbar.</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="sa-user-save">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// CLUBS
// -----------------------------------------------------------------------------
function ClubsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/clubs");
      setItems(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => !search.trim() || c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="sa-clubs">
      <SearchBar value={search} onChange={setSearch} placeholder="Verein…" testId="sa-clubs-search" />
      {loading ? <Loading /> : (
        <div className="space-y-2 mt-3">
          {filtered.length === 0 && <Empty />}
          {filtered.map((c) => (
            <div key={c.id} className="rounded-md border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`sa-club-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-10 w-10 bg-brand/20 flex items-center justify-center rounded-md shrink-0">
                    {c.logo_url
                      ? <img src={c.logo_url} alt="" className="h-8 w-8 object-contain" />
                      : <Building2 className="h-5 w-5 text-brand-lighter" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-zinc-100 truncate">{c.name}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                      {c.teams_count} Mannschaft{c.teams_count === 1 ? "" : "en"} · {c.users_count} Nutzer
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditItem(c)}
                    className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800"
                    data-testid={`sa-club-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDelItem(c)}
                    className="text-zinc-400 hover:text-red-400 hover:bg-zinc-800"
                    data-testid={`sa-club-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ClubEditDialog item={editItem}
        onClose={() => setEditItem(null)} onSaved={async () => { setEditItem(null); await load(); }} />
      <ConfirmDeleteDialog item={delItem}
        title={`Verein "${delItem?.name}" löschen?`}
        description="ALLE Mannschaften, Spieler, Trainings, Spiele und Turniere dieses Vereins werden permanent gelöscht."
        danger
        onClose={() => setDelItem(null)}
        onConfirm={async () => {
          try {
            await api.delete(`/super/clubs/${delItem.id}`);
            toast.success("Verein gelöscht");
            setDelItem(null);
            await load();
          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen"); }
        }} />
    </div>
  );
}

function ClubEditDialog({ item, onClose, onSaved }) {
  const [name, setName] = useState(""); const [logo, setLogo] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (item) { setName(item.name); setLogo(item.logo_url || ""); } }, [item]);
  if (!item) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/super/clubs/${item.id}`, { name: name.trim(), logo_url: logo.trim() || null });
      toast.success("Verein aktualisiert"); await onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle>Verein bearbeiten</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">{item.id}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="sa-club-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Logo-URL (optional)</Label>
            <Input value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://…"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="sa-club-logo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="sa-club-save">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// TEAMS
// -----------------------------------------------------------------------------
function TeamsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/teams");
      setItems(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (t.name + " " + t.club_name).toLowerCase().includes(q);
  });

  return (
    <div data-testid="sa-teams">
      <SearchBar value={search} onChange={setSearch} placeholder="Mannschaft, Verein…" testId="sa-teams-search" />
      {loading ? <Loading /> : (
        <div className="space-y-2 mt-3">
          {filtered.length === 0 && <Empty />}
          {filtered.map((t) => (
            <div key={t.id}
              className={"rounded-md border bg-zinc-900/40 p-4 " + (t.active ? "border-zinc-800" : "border-zinc-800/50 opacity-60")}
              data-testid={`sa-team-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-10 w-10 bg-brand/20 flex items-center justify-center rounded-md shrink-0">
                    <Shield className="h-5 w-5 text-brand-lighter" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-zinc-100 truncate">{t.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{t.club_name}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                      {t.active ? "Aktiv" : "Deaktiviert"} · {t.players_count} Spieler
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditItem(t)}
                    className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800"
                    data-testid={`sa-team-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDelItem(t)}
                    className="text-zinc-400 hover:text-red-400 hover:bg-zinc-800"
                    data-testid={`sa-team-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <TeamEditDialog item={editItem}
        onClose={() => setEditItem(null)} onSaved={async () => { setEditItem(null); await load(); }} />
      <ConfirmDeleteDialog item={delItem}
        title={`Mannschaft "${delItem?.name}" löschen?`}
        description="Alle Spieler, Trainings, Spiele und Turniere dieser Mannschaft werden permanent gelöscht."
        danger
        onClose={() => setDelItem(null)}
        onConfirm={async () => {
          try {
            await api.delete(`/super/teams/${delItem.id}`);
            toast.success("Mannschaft gelöscht");
            setDelItem(null);
            await load();
          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen"); }
        }} />
    </div>
  );
}

function TeamEditDialog({ item, onClose, onSaved }) {
  const [name, setName] = useState(""); const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (item) { setName(item.name); setActive(!!item.active); } }, [item]);
  if (!item) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/super/teams/${item.id}`, { name: name.trim(), active });
      toast.success("Mannschaft aktualisiert"); await onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle>Mannschaft bearbeiten</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">{item.club_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md" data-testid="sa-team-name" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="text-sm text-zinc-100">Aktiv</div>
            <input type="checkbox" checked={active}
              onChange={(e) => setActive(e.target.checked)} className="accent-brand h-4 w-4" data-testid="sa-team-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="sa-team-save">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Shared bits
// -----------------------------------------------------------------------------
function SearchBar({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="bg-zinc-900 border-zinc-800 text-zinc-100 h-10 rounded-md pl-9" data-testid={testId} />
    </div>
  );
}

function Loading() { return <div className="text-zinc-500 text-sm mt-4">Lädt…</div>; }
function Empty() {
  return <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-center text-xs text-zinc-500">Keine Einträge.</div>;
}

function ConfirmDeleteDialog({ item, title, description, danger, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const handle = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {danger && <AlertTriangle className="h-5 w-5 text-red-400" />}
            {title}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 pt-2">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
          <Button onClick={handle} disabled={busy}
            className="bg-red-600 hover:bg-red-500 text-white rounded-md" data-testid="sa-confirm-delete">
            {busy ? "Lösche…" : "Permanent löschen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
